import React, { useEffect, useMemo, useState } from 'react';
import { Trophy, Medal, Award, Star, Clock, Calendar as CalIcon, Target, Square as SquareIcon } from 'lucide-react';
import { classifiche as classificheMock, categorie as categorieMock } from '../data/mock';
import SocialShare from '../components/SocialShare';
import { api } from '../api/client';
import { StandingsTables, buildResolveContext, resolveEventInDay, resolveFinaleHeaderForEvent, detectCategoriaFromText, buildFinalRanking } from '../components/admin/TornaiEditor';
import { useSiteText } from '../hooks/useSiteText';

const CAT_KEYS = { 'Pulcini 2015': 'pulcini-2015', 'Pulcini 2016': 'pulcini-2016', 'Primi Calci 2017/18': 'primi-calci' };

// Normalizza un nome categoria per dedup (es. "PULCINI-2015", "Pulcini 2015", "pulcini2015" → "pulcini2015")
const normCat = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const CANONICAL_LABEL = {
  [normCat('Pulcini 2015')]: 'Pulcini 2015',
  [normCat('Pulcini 2016')]: 'Pulcini 2016',
  [normCat('Primi Calci 2017/18')]: 'Primi Calci 2017/18',
  // Alias chiave breve usata storicamente nel DB
  [normCat('primi-calci')]: 'Primi Calci 2017/18',
};

// Lookup normalizzato per risolvere la chiave del torneo da QUALUNQUE variante
// del nome categoria (label "Primi Calci 2017/18", chiave breve "primi-calci",
// versioni uppercase, ecc.) → restituisce sempre la chiave canonica del torneo.
const CAT_KEYS_NORM = (() => {
  const m = {};
  Object.entries(CAT_KEYS).forEach(([label, key]) => {
    m[normCat(label)] = key;
    m[normCat(key)] = key;
  });
  // Alias supplementari per varianti note nei DB di produzione
  m[normCat('primi-calci 2017/18')] = 'primi-calci';
  m[normCat('primi calci')] = 'primi-calci';
  return m;
})();

export default function Classifiche() {
  const [classifiche, setClassifiche] = useState(classificheMock);
  const [calendario, setCalendario] = useState([]);
  const [tournament, setTournament] = useState({});

  useEffect(() => {
    api.getClassifiche().then((obj) => { if (obj && Object.keys(obj).length > 0) setClassifiche(obj); }).catch(() => {});
    api.getCalendarioFull().then((rows) => setCalendario(Array.isArray(rows) ? rows : [])).catch(() => setCalendario([]));
    api.getTournament().then(setTournament).catch(() => setTournament({}));
  }, []);

  // Costruisci elenco di tab dinamico: usa le categorie effettivamente presenti
  // (sia "vecchie" classifiche che dati torneo attivi). Dedup case-insensitive ignorando
  // separatori, così "PULCINI-2015", "Pulcini 2015" e "pulcini2015" diventano una sola tab.
  const tabs = useMemo(() => {
    const fromCl = Object.keys(classifiche || {});
    const fromT = Object.entries(tournament || {})
      .filter(([, t]) => t && t.active)
      .map(([k]) => Object.keys(CAT_KEYS).find((label) => CAT_KEYS[label] === k))
      .filter(Boolean);
    const seen = new Map(); // norm → { key (preferito), label }
    [...fromCl, ...fromT].forEach((raw) => {
      const n = normCat(raw);
      if (!n) return;
      const niceLabel = CANONICAL_LABEL[n] || raw;
      if (!seen.has(n)) seen.set(n, { key: raw, label: niceLabel });
      else if (CANONICAL_LABEL[n] && seen.get(n).label !== CANONICAL_LABEL[n]) {
        // Aggiorna l'etichetta a quella canonica più leggibile
        seen.set(n, { key: seen.get(n).key, label: CANONICAL_LABEL[n] });
      }
    });
    if (seen.size === 0) return categorieMock.map((c) => ({ key: c.label, label: c.label }));
    return Array.from(seen.values());
  }, [classifiche, tournament]);

  const [tab, setTab] = useState(null);
  useEffect(() => { if (tabs.length && (!tab || !tabs.some((t) => t.key === tab))) setTab(tabs[0].key); }, [tabs, tab]);

  // Etichetta leggibile per la tab corrente (canonica se disponibile)
  const curLabel = useMemo(() => {
    if (!tab) return '';
    const found = tabs.find((t) => t.key === tab);
    return found ? found.label : (CANONICAL_LABEL[normCat(tab)] || tab);
  }, [tab, tabs]);

  // Risolvi i dati della tab corrente: prima cerca per chiave esatta nel DB, poi nel mock
  const cur = (() => {
    if (!tab) return { categoria: '', ranking: [], finale: '', premi: [] };
    if (classifiche[tab]) return { ...classifiche[tab], categoria: curLabel };
    // Fallback su mock se il DB non ha la categoria ma la usiamo dal mock (nome chiave diverso)
    const mockKey = Object.keys(classificheMock).find((k) => classificheMock[k].categoria === tab) || Object.keys(classificheMock)[0];
    return { ...(classificheMock[mockKey] || { ranking: [], finale: '', premi: [] }), categoria: curLabel };
  })();

  // Mappatura categoria → etichetta leggibile
  const CAT_LABEL = { 'pulcini-2015': 'Pulcini 2015', 'pulcini-2016': 'Pulcini 2016', 'primi-calci': 'Primi Calci 2017/18' };

  // Contesto risoluzione placeholder (1° Girone A, Win. Gir. X, ecc.)
  const resolveCtx = useMemo(() => buildResolveContext(calendario), [calendario]);

  // Trova i risultati delle partite (dal calendario) — ogni evento con risultato
  const risultatiPartite = useMemo(() => {
    const out = [];
    (calendario || []).forEach((day) => {
      const cats = String(day.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
      (day.eventi || []).forEach((e) => {
        if (e.risultato && String(e.risultato).trim()) {
          const eventCat = detectCategoriaFromText(e.evento);
          out.push({
            giorno: day.giorno,
            data: day.data,
            ora: e.ora,
            evento: resolveEventInDay(e.evento, day, resolveCtx),
            finaleHeader: resolveFinaleHeaderForEvent(day.giorno, e.evento, day, resolveCtx),
            luogo: e.luogo,
            risultato: e.risultato,
            categorie: (eventCat ? [eventCat] : cats).map((c) => CAT_LABEL[c] || c),
          });
        }
      });
    });
    return out;
  }, [calendario, resolveCtx]);

  // Risultati filtrati per categoria attiva
  const risultatiPartiteCur = useMemo(() => {
    if (!curLabel) return [];
    const target = normCat(curLabel);
    return risultatiPartite.filter((r) => (r.categorie || []).some((c) => normCat(c) === target));
  }, [risultatiPartite, curLabel]);

  const medalColor = (pos) => {
    if (pos === 1) return 'from-yellow-400 to-yellow-600';
    if (pos === 2) return 'from-gray-300 to-gray-500';
    if (pos === 3) return 'from-orange-500 to-orange-700';
    return 'from-navy-light to-navy';
  };

  const title = useSiteText('classifiche.title', 'Classifiche');
  const subtitle = useSiteText('classifiche.subtitle', 'Risultati e classifiche del XIV Memorial Eugenio Abbro');
  const waitTitle = useSiteText('classifiche.wait_title', 'Classifica in attesa');
  const premiTitle = useSiteText('classifiche.premi_title', 'Premi Speciali');

  // Determina dinamicamente le colonne extra (oltre pos/team) per la categoria corrente
  const extraCols = useMemo(() => {
    const set = new Set();
    (cur.ranking || []).forEach((r) => Object.keys(r).forEach((k) => {
      if (!['pos', 'team', 'id', 'highlight'].includes(k)) set.add(k);
    }));
    return Array.from(set);
  }, [cur.ranking]);

  return (
    <div className="bg-navy min-h-screen">
      <section className="relative py-24">
        <div className="absolute inset-0 bg-gradient-to-b from-navy-dark to-navy"></div>
        <div className="relative max-w-5xl mx-auto px-4 lg:px-8 text-center">
          <Trophy size={48} className="text-pink-brand mx-auto mb-4" />
          <h1 className="font-brand text-5xl md:text-6xl font-bold text-white mb-4">{title}</h1>
          <div className="w-24 h-1 bg-pink-brand mx-auto rounded"></div>
          <p className="text-white/70 mt-6 max-w-2xl mx-auto">
            {subtitle}
          </p>
          <div className="mt-6 flex justify-center">
            <SocialShare title="Classifiche XIV Memorial Eugenio Abbro" />
          </div>
        </div>
      </section>

      <section className="pb-20">
        <div className="max-w-4xl mx-auto px-4 lg:px-8">
          {/* Tab */}
          {tabs.length > 1 && (
            <div className="flex flex-wrap gap-2 mb-6 bg-navy-dark p-1.5 rounded-2xl border border-white/10">
              {tabs.map((c) => (
                <button
                  key={c.key}
                  onClick={() => setTab(c.key)}
                  className={`flex-1 min-w-[140px] py-3 px-4 rounded-xl font-bold text-xs sm:text-sm uppercase tracking-wider transition-all ${
                    tab === c.key ? 'bg-pink-brand text-white shadow-lg' : 'text-white/70 hover:text-white'
                  }`}
                >
                  {c.label}
                </button>
              ))}
            </div>
          )}

          {/* Risultati partite della categoria attiva (filtrati dal calendario) */}
          {risultatiPartiteCur.length > 0 && (
            <div className="mb-8">
              <h2 className="font-display text-2xl text-white uppercase font-bold mb-4 flex items-center gap-3">
                <CalIcon size={22} className="text-pink-brand" /> Risultati partite · {curLabel}
              </h2>
              <div className="glass-card overflow-hidden divide-y divide-white/10">
                {risultatiPartiteCur.map((r, i) => (
                  <div key={i} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-2 sm:gap-4">
                    <div className="text-pink-brand text-xs font-bold uppercase tracking-widest sm:w-44 flex-shrink-0">
                      <div>{r.giorno} <span className="text-white/60 font-normal">{r.ora}</span></div>
                    </div>
                    <div className="flex-1 text-white text-sm">
                      <div>{r.evento}</div>
                      {r.finaleHeader && (
                        <div className="text-pink-brand text-xs font-semibold mt-0.5">{r.finaleHeader}</div>
                      )}
                    </div>
                    <div className="bg-pink-brand text-white text-sm font-bold px-3 py-1.5 rounded inline-block sm:ml-auto">{r.risultato}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Finalisti della categoria (auto-popolati dal calendario)
              Cerca una giornata della categoria corrente che contiene un placeholder
              "Win. Gir. X" nel campo giorno e lo risolve in nomi reali. */}
          {(() => {
            const catKey = CAT_KEYS_NORM[normCat(tab)] || CAT_KEYS[tab] || tab;
            for (const day of (calendario || [])) {
              const cats = String(day.categoria || '').split(',').map((s) => s.trim()).filter(Boolean);
              if (!cats.includes(catKey)) continue;
              const giornoStr = String(day.giorno || '');
              if (!/Win\.?|Vinc/i.test(giornoStr)) continue;
              const resolved = resolveEventInDay(giornoStr, { ...day, categoria: catKey }, resolveCtx);
              if (resolved === giornoStr) continue;
              // Trova l'evento di questa categoria (per orario)
              const ev = (day.eventi || []).find((e) => detectCategoriaFromText(e.evento) === catKey) || (day.eventi || [])[0];
              return (
                <div className="glass-card p-5 mb-6 text-center bg-pink-brand/10 border-pink-brand/40">
                  <div className="text-pink-brand text-xs uppercase font-bold tracking-widest mb-2 flex items-center justify-center gap-2">
                    <Trophy size={14} /> Finalisti · {curLabel}
                  </div>
                  <h2 className="text-white font-bold text-base sm:text-lg">{resolved}</h2>
                  {(day.data || ev?.ora) && (
                    <div className="text-white/60 text-xs mt-1">
                      {[day.data, ev?.ora].filter(Boolean).join(' · ')}
                      {ev?.luogo ? ` · ${ev.luogo}` : ''}
                    </div>
                  )}
                </div>
              );
            }
            return null;
          })()}

          {/* Pannello torneo (gironi auto + marcatori + cartellini) */}
          {(() => {
            const catKey = CAT_KEYS_NORM[normCat(tab)] || CAT_KEYS[tab] || tab;
            const t = tournament[catKey];
            if (!t || !t.active) return null;
            const marcSorted = [...(t.marcatori || [])].sort((a, b) => Number(b.reti || 0) - Number(a.reti || 0));
            // Match della categoria (esclude semifinali con tag ">>> Girone X")
            const autoMatches = resolveCtx.matchesByCat?.[catKey] || [];
            const matchesToShow = autoMatches.length > 0 ? autoMatches : (t.matches || []);
            return (
              <div className="space-y-6 mb-8">
                <div className="glass-card p-5">
                  <h3 className="font-display text-xl text-white uppercase font-bold mb-3 flex items-center gap-2"><Trophy size={20} className="text-pink-brand" /> Classifica Gironi</h3>
                  <StandingsTables matches={matchesToShow} />
                </div>

                {marcSorted.length > 0 && (
                  <div className="glass-card p-5">
                    <h3 className="font-display text-xl text-white uppercase font-bold mb-3 flex items-center gap-2"><Target size={20} className="text-pink-brand" /> Classifica Marcatori</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-white/60 text-[10px] uppercase tracking-widest border-b border-white/15">
                            <th className="px-2 py-2 w-10 text-center">#</th>
                            <th className="px-2 py-2 text-left">Squadra</th>
                            <th className="px-2 py-2 text-left">Calciatore</th>
                            <th className="px-2 py-2 w-12 text-center font-bold text-pink-brand">RETI</th>
                          </tr>
                        </thead>
                        <tbody>
                          {marcSorted.map((m, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="px-2 py-1.5 text-center">
                                <span className="w-6 h-6 rounded-full bg-pink-brand/20 border border-pink-brand text-pink-brand text-xs font-bold inline-flex items-center justify-center">{i + 1}</span>
                              </td>
                              <td className="px-2 py-1.5 text-white/80">{m.squadra}</td>
                              <td className="px-2 py-1.5 text-white font-semibold">{m.calciatore}</td>
                              <td className="px-2 py-1.5 text-pink-brand text-center font-bold">{m.reti}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}

                {(t.cartellini || []).length > 0 && (
                  <div className="glass-card p-5">
                    <h3 className="font-display text-xl text-white uppercase font-bold mb-3 flex items-center gap-2"><SquareIcon size={20} className="text-yellow-400" /> Ammoniti & Espulsi</h3>
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-white/60 text-[10px] uppercase tracking-widest border-b border-white/15">
                            <th className="px-2 py-2 text-left">Squadra</th>
                            <th className="px-2 py-2 text-left">Calciatore</th>
                            <th className="px-2 py-2 w-24 text-center">Tipo</th>
                            <th className="px-2 py-2 text-left">Motivo</th>
                          </tr>
                        </thead>
                        <tbody>
                          {(t.cartellini || []).map((c, i) => (
                            <tr key={i} className="border-b border-white/5">
                              <td className="px-2 py-1.5 text-white/80">{c.squadra}</td>
                              <td className="px-2 py-1.5 text-white font-semibold">{c.calciatore}</td>
                              <td className="px-2 py-1.5 text-center">
                                <span className={`inline-block px-2 py-0.5 rounded text-[10px] font-bold uppercase ${c.tipo === 'rosso' ? 'bg-red-500/20 text-red-400 border border-red-500/40' : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/40'}`}>
                                  {c.tipo === 'rosso' ? 'Rosso' : 'Giallo'}
                                </span>
                              </td>
                              <td className="px-2 py-1.5 text-white/70 text-xs">{c.motivo || '—'}</td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  </div>
                )}
              </div>
            );
          })()}

          {cur.finale && (
            <div className="glass-card p-5 mb-6 text-center">
              <div className="text-white/60 text-xs uppercase font-bold tracking-widest mb-1">Finale categoria</div>
              <h2 className="text-white font-bold text-base">{cur.finale}</h2>
            </div>
          )}

          {/* Ranking */}
          {(() => {
            const catKey = CAT_KEYS_NORM[normCat(tab)] || CAT_KEYS[tab] || tab;
            // Classifica finale auto-derivata dal calendario (gironi + semifinali + finale)
            const autoRanking = buildFinalRanking(catKey, calendario, resolveCtx);
            // Usa: 1) ranking freeform admin se presente, 2) altrimenti auto, 3) altrimenti vuoto
            const effectiveRanking = (cur.ranking && cur.ranking.length > 0) ? cur.ranking : (autoRanking || []);
            const isAuto = !(cur.ranking && cur.ranking.length > 0) && autoRanking && autoRanking.length > 0;
            return (
          <div className="glass-card overflow-hidden">
            <div className="bg-pink-brand px-5 py-3 flex items-center justify-between">
              <h3 className="text-white font-bold uppercase tracking-wider text-sm">Classifica · {cur.categoria || tab}</h3>
              <Trophy size={18} className="text-white" />
            </div>
            {effectiveRanking.length === 0 ? (
              <div className="p-10 text-center">
                <Clock size={40} className="text-white/30 mx-auto mb-4" />
                <h3 className="text-white font-bold text-lg mb-2">{waitTitle}</h3>
                <p className="text-white/60 text-sm max-w-md mx-auto">
                  I risultati della categoria {cur.categoria || tab} saranno pubblicati al termine delle partite del XIV Memorial.
                </p>
              </div>
            ) : (
              <>
                {isAuto && (
                  <div className="bg-pink-brand/10 border-b border-pink-brand/30 px-5 py-2 text-pink-brand text-[11px] uppercase tracking-widest font-bold text-center" data-testid="auto-ranking-badge">
                    Classifica calcolata automaticamente dai risultati
                  </div>
                )}
                <div className="divide-y divide-white/10">
                  {effectiveRanking.map((r, i) => (
                    <div
                      key={i}
                      className={`px-5 py-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-white/5 transition-colors ${(r.highlight || r.pos === 1) ? 'bg-pink-brand/10' : ''}`}
                    >
                      <div className={`w-10 h-10 rounded-full bg-gradient-to-br ${medalColor(r.pos)} flex items-center justify-center text-white font-bold flex-shrink-0`}>
                        {r.pos <= 3 ? <Medal size={18} /> : r.pos}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="text-white font-semibold break-words">{r.team}</div>
                        {r.pos === 1 && (
                          <div className="text-pink-brand text-xs font-bold uppercase tracking-widest flex items-center gap-1 mt-0.5">
                            <Star size={11} fill="currentColor" /> Vincitore
                          </div>
                        )}
                        {r.fase === 'finale-2' && (
                          <div className="text-white/60 text-xs uppercase tracking-widest mt-0.5">Finalista</div>
                        )}
                        {r.fase === 'semifinale' && (
                          <div className="text-white/60 text-xs uppercase tracking-widest mt-0.5">Semifinalista</div>
                        )}
                      </div>
                      {/* Render colonne extra dinamiche (ranking freeform) */}
                      <div className="flex flex-wrap gap-2 sm:ml-auto">
                        {extraCols.map((col) => (
                          r[col] ? (
                            <div key={col} className="bg-navy-dark/70 border border-white/10 rounded px-2.5 py-1 text-xs">
                              <span className="text-white/40 uppercase tracking-widest text-[10px] mr-1">{col}</span>
                              <span className="text-white font-bold">{r[col]}</span>
                            </div>
                          ) : null
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
            );
          })()}

          {/* Premi */}
          {cur.premi && cur.premi.length > 0 && (
            <div className="mt-8">
              <h3 className="font-display text-2xl text-white uppercase font-bold mb-5 flex items-center gap-3">
                <Award size={22} className="text-pink-brand" />
                {premiTitle}
              </h3>
              <div className="grid sm:grid-cols-3 gap-4">
                {cur.premi.filter((p) => p && (typeof p === 'string' ? p.trim() : (p.label || p.team))).map((p, i) => (
                  <div key={i} className="glass-card p-5 text-center">
                    <div className="w-12 h-12 mx-auto mb-3 rounded-full bg-pink-brand/20 border-2 border-pink-brand flex items-center justify-center">
                      <Award size={20} className="text-pink-brand" />
                    </div>
                    {typeof p === 'string' ? (
                      <div className="text-white font-semibold text-sm">{p}</div>
                    ) : (
                      <>
                        <div className="text-white/60 text-xs uppercase font-bold tracking-widest mb-1">{p.label}</div>
                        <div className="text-white font-bold">{p.team}</div>
                      </>
                    )}
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
